import React, { useContext, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import UnifiedMatchContext from '../../context/matches/UnifiedMatchContext';
import { getPokemonSpriteUrl } from '../../api/pokeApi'; // For opponent sprite handling
import EditNotesModal from './EditNotesModal';
import ConfirmModal from '../layout/ConfirmModal';
import './MatchItem.css';
import './LogMatch.css';
import './MatchItemMobile.css';
import './MatchItemModern.css';

const MatchItemTCG = ({ match, deleteMatch = null, useModernUI = true }) => {
  const { 
    _id, 
    opponentDeck, 
    opponentDeckVariant,
    activeDeck, 
    deckName,
    games,
    notes,
    tags,
    createdAt,
    format,
    // Player deck sprites
    deckSprite: deckSpriteFromMatch,
    secondaryDeckSprite: secondaryDeckSpriteFromMatch,
    // Opponent sprites
    opponentDeckSprite: opponentDeckSpriteFromMatch,
    opponentSecondaryDeckSprite: opponentSecondaryDeckSpriteFromMatch,
    // Reference to full deck
    deck,
    // Group information
    group
  } = match;
  const { deleteTCGMatch, updateTCGMatch } = useContext(UnifiedMatchContext);
  const navigate = useNavigate();
  // Player deck sprite state
  const [playerDeckSprite, setPlayerDeckSprite] = useState('');
  const [playerSecondaryDeckSprite, setPlayerSecondaryDeckSprite] = useState('');
  // Opponent deck sprite state
  const [opponentDeckSprite, setOpponentDeckSprite] = useState('');
  const [opponentSecondaryDeckSprite, setOpponentSecondaryDeckSprite] = useState('');
  const [showEditNotesModal, setShowEditNotesModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showGameDetailsModal, setShowGameDetailsModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = async (id) => {
    if (match.gameType === 'TCG' && deleteTCGMatch) {
      await deleteTCGMatch(id);
    } else if (deleteMatch) {
      deleteMatch(id);
    }
    setShowDeleteConfirm(false);
  };

  const handleEditNotes = (e) => {
    // Stop propagation to prevent any parent handlers from firing
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setShowEditNotesModal(true);
  };
  
  const handleSaveNotes = async (updatedNotes) => {
    try {
      // Create a copy of the match with updated notes
      const updatedMatch = {
        ...match,
        notes: updatedNotes
      };
      
      // Update the match in the database
      await updateTCGMatch(updatedMatch);
    } catch (error) {
      console.error('Error updating match notes:', error);
      alert('Failed to save match notes. Please try again.');
      return; // Don't close the modal if save failed
    }
    
    // Only close the modal if save succeeded
    setShowEditNotesModal(false);
  };
  
  const handleGameClick = (e, game, index) => {
    // Stop propagation to prevent expanding/collapsing the match item
    e.stopPropagation();
    
    // Set the selected game and show the modal
    setSelectedGame({...game, gameNumber: index + 1});
    setShowGameDetailsModal(true);
  };
  
  const handleEditMatch = (e) => {
    // Stop propagation to prevent any parent handlers from firing
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    // For PTCGL-imported matches, we need to ensure they can be fully edited
    // by removing any import-specific properties that might restrict editing
    const matchForEdit = {...match};

    // Remove PTCGL import flags to ensure full editing capability
    if (matchForEdit.importSource === 'ptcgl') {
      delete matchForEdit.importSource;
      delete matchForEdit.ptcglLogText;

      // If there are any other PTCGL-specific properties, remove them too
      console.log('Preparing PTCGL-imported match for full editing');
    }

    // Navigate to the LogMatchTCG component with the current match data
    const editPath = `/edit-match/tcg/${_id}`;
    navigate(editPath, { state: { matchToEdit: matchForEdit } });
  };
  
  const closeGameDetailsModal = () => {
    setShowGameDetailsModal(false);
    setSelectedGame(null);
  };

  const wins = games?.filter(g => g.result === 'win')?.length || 0;
  const losses = games?.filter(g => g.result === 'loss')?.length || 0;
  const matchResult = {
    result: wins > losses ? 'Won' : 'Lost',
    score: `${wins}-${losses}`,
    status: wins > losses ? 'success' : 'danger'
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

  // Extract the user's deck name properly, considering all potential data structures
  const userDeckName = (() => {
    // Handle case where activeDeck is a string (simple case)
    if (typeof activeDeck === 'string') return activeDeck;
    
    // Handle case where activeDeck is an array (common in TCGMatch model)
    if (Array.isArray(activeDeck) && activeDeck.length > 0) return activeDeck[0];
    
    // Handle case where activeDeck is an object with a name property
    if (activeDeck && typeof activeDeck === 'object' && activeDeck.name) return activeDeck.name;
    
    // Try other possible locations
    if (match.deckName) return match.deckName;
    
    // If deck reference exists and has a name
    if (deck && deck.name) return deck.name;
    
    // Last resort fallback
    return 'Unknown Deck';
  })();
  
  // Effect to load all deck sprites (both player and opponent)
  useEffect(() => {
    const fetchSprites = async () => {
      // Handle player deck sprites
      // Prioritize direct match destructured values
      if (deckSpriteFromMatch && deckSpriteFromMatch.url) {
        setPlayerDeckSprite(deckSpriteFromMatch.url);
        
        // Check if there's a secondary player sprite
        if (secondaryDeckSpriteFromMatch && secondaryDeckSpriteFromMatch.url) {
          setPlayerSecondaryDeckSprite(secondaryDeckSpriteFromMatch.url);
        }
      }
      // Try full match object as fallback for player sprites
      else if (match.deckSprite && match.deckSprite.url) {
        setPlayerDeckSprite(match.deckSprite.url);
        
        // Check if there's a secondary player sprite
        if (match.secondaryDeckSprite && match.secondaryDeckSprite.url) {
          setPlayerSecondaryDeckSprite(match.secondaryDeckSprite.url);
        }
      }
      // If deck has sprites, use them as another fallback
      else if (deck && deck.pokemonSprite && deck.pokemonSprite.url) {
        setPlayerDeckSprite(deck.pokemonSprite.url);
        
        if (deck.secondaryPokemonSprite && deck.secondaryPokemonSprite.url) {
          setPlayerSecondaryDeckSprite(deck.secondaryPokemonSprite.url);
        }
      }
      
      // Handle opponent deck sprites
      if (opponentDeck) {
        // Prioritize direct match.opponentDeckSprite field
        if (match.opponentDeckSprite && match.opponentDeckSprite.url) {
          // Use the pre-defined sprite directly from the match object
          setOpponentDeckSprite(match.opponentDeckSprite.url);
          
          // Check if there's a secondary sprite
          if (match.opponentSecondaryDeckSprite && match.opponentSecondaryDeckSprite.url) {
            setOpponentSecondaryDeckSprite(match.opponentSecondaryDeckSprite.url);
          }
        }
        // Try the renamed destructured variables as fallback
        else if (opponentDeckSpriteFromMatch && opponentDeckSpriteFromMatch.url) {
          setOpponentDeckSprite(opponentDeckSpriteFromMatch.url);
          
          // Check if there's a secondary sprite
          if (opponentSecondaryDeckSpriteFromMatch && opponentSecondaryDeckSpriteFromMatch.url) {
            setOpponentSecondaryDeckSprite(opponentSecondaryDeckSpriteFromMatch.url);
          }
        } 
        // Last resort: Generate from deck name
        else {
          try {
            const url = await getPokemonSpriteUrl(opponentDeck);
            if (url) {
              setOpponentDeckSprite(url);
            } else {
              // Default to Pikachu if no sprite is found
              setOpponentDeckSprite('https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/25.png');
            }
          } catch (error) {
            console.error("Error fetching opponent sprite:", error);
            setOpponentDeckSprite('https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/25.png');
          }
        }
      }
    };
    
    fetchSprites();
  }, [
    // Dependencies for player sprites
    deckSpriteFromMatch,
    secondaryDeckSpriteFromMatch,
    match.deckSprite,
    match.secondaryDeckSprite,
    deck,
    // Dependencies for opponent sprites
    opponentDeck,
    opponentDeckSpriteFromMatch,
    opponentSecondaryDeckSpriteFromMatch,
    match.opponentDeckSprite,
    match.opponentSecondaryDeckSprite
  ]);

  if (useModernUI) {
    return (
      <div className={`match-item-modern`} onClick={() => {
        if (games && games.length > 0) {
          setSelectedGame({...games[0], gameNumber: 1});
        } else {
          setSelectedGame({ gameNumber: 1 });
        }
        setShowGameDetailsModal(true);
      }}>
        {/* Match Header */}
        <div className="match-header-modern">
          <span className={`match-result-badge ${matchResult.result.toLowerCase()}`}>
            {matchResult.result} {matchResult.score}
          </span>
          <span className="match-format-badge">
            TCG • {format || 'Bo1'}
          </span>
          {match.importSource === 'ptcgl' && (
            <span className="match-device-badge">
              <i className="fas fa-desktop"></i>
              PTCGL
            </span>
          )}
          {group && (
            <span className="match-format-badge">
              <i className="fas fa-object-group"></i> {group.name}
            </span>
          )}
          {tags && tags.length > 0 && tags.map((tag, index) => (
            <span key={index} className="match-tag-badge">
              <i className="fas fa-tag"></i> {tag}
            </span>
          ))}
        </div>

        {/* Match Meta Info */}
        <div className="match-meta-info">
          <div className="match-meta-item">
            <i className="far fa-calendar-alt"></i>
            <span>{formatDate(createdAt)}</span>
          </div>
          <div className="match-meta-item match-turn-info">
            <span>Went {games?.[0]?.wentFirst ? 'First' : 'Second'}</span>
            {games?.[0]?.coinFlipWin !== undefined && (
              <span>• Coin flip: {games[0].coinFlipWin ? 'Won' : 'Lost'}</span>
            )}
          </div>
        </div>

        {/* Deck Information */}
        <div className="match-deck-section">
          <div className="deck-column">
            <span className="deck-label">Your Deck</span>
            <div className="deck-row">
              <div className="deck-info">
                <span className="deck-tag deck-tag--you">You:</span>
                {playerDeckSprite && (
                  <div className="deck-sprites">
                    <img src={playerDeckSprite} alt="" className="deck-sprite" />
                    {playerSecondaryDeckSprite && (
                      <img src={playerSecondaryDeckSprite} alt="" className="deck-sprite" />
                    )}
                  </div>
                )}
                <span className="deck-name">{userDeckName}</span>
              </div>
            </div>
          </div>
          <div className="deck-column">
            <span className="deck-label">Opponent's Deck</span>
            <div className="deck-row">
              <div className="deck-info">
                <span className="deck-tag deck-tag--opp">Opp:</span>
                {opponentDeckSprite && (
                  <div className="deck-sprites">
                    <img src={opponentDeckSprite} alt="" className="deck-sprite" />
                    {opponentSecondaryDeckSprite && (
                      <img src={opponentSecondaryDeckSprite} alt="" className="deck-sprite" />
                    )}
                  </div>
                )}
                <span className="deck-name">{opponentDeck || 'Unknown'}</span>
                {opponentDeckVariant && (
                  <span className="deck-variant">({opponentDeckVariant})</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="match-actions">
          <button
            className="match-action-btn edit"
            onClick={(e) => {
              e.stopPropagation();
              handleEditMatch(e);
            }}
            title="Edit Match"
          >
            <i className="fas fa-edit"></i>
          </button>
          {(deleteMatch || deleteTCGMatch) && (
            <button
              className="match-action-btn delete"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              title="Delete Match"
            >
              <i className="fas fa-trash"></i>
            </button>
          )}
        </div>

        
        {/* Keep existing modals */}
        {showGameDetailsModal && selectedGame && (() => {
          const modalRoot = document.getElementById('modal-root');
          if (!modalRoot) {
            console.warn("modal-root element not found, creating it dynamically");
            const newModalRoot = document.createElement('div');
            newModalRoot.id = 'modal-root';
            document.body.appendChild(newModalRoot);
          }
          
          return ReactDOM.createPortal(
          <div className="notes-modal-backdrop" onClick={closeGameDetailsModal}>
            <div 
              className="notes-modal" 
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '700px' }}
            >
              <div className="notes-modal-header">
                <h3>Match Details</h3>
                <div className="notes-modal-controls">
                  <button 
                    className="notes-modal-control-btn" 
                    onClick={handleEditMatch} 
                    title="Edit Match"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button className="notes-modal-close" onClick={closeGameDetailsModal}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
              
              {/* Game Tabs for multi-game matches */}
              {games && games.length > 1 && (
                <div style={{
                  display: 'flex',
                  borderBottom: '2px solid var(--border-color)',
                  backgroundColor: 'var(--bg-secondary)',
                  padding: '0 20px',
                  gap: '10px'
                }}>
                  {games.map((game, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedGame({...game, gameNumber: index + 1})}
                      style={{
                        padding: '12px 20px',
                        border: 'none',
                        backgroundColor: selectedGame?.gameNumber === index + 1 ? 'var(--bg-primary)' : 'transparent',
                        color: selectedGame?.gameNumber === index + 1 ? 'var(--text-primary)' : 'var(--text-secondary)',
                        borderBottom: selectedGame?.gameNumber === index + 1 ? '2px solid var(--link-color)' : '2px solid transparent',
                        marginBottom: '-2px',
                        cursor: 'pointer',
                        fontWeight: selectedGame?.gameNumber === index + 1 ? 'bold' : 'normal',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedGame?.gameNumber !== index + 1) {
                          e.target.style.backgroundColor = 'var(--link-hover-bg)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedGame?.gameNumber !== index + 1) {
                          e.target.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <span>Game {index + 1}</span>
                      <span style={{
                        fontSize: '0.8rem',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: game.result === 'win' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                        color: game.result === 'win' ? '#4CAF50' : '#F44336'
                      }}>
                        {game.result === 'win' ? 'W' : 'L'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              
              <div className="notes-modal-body tcg-modal-body" style={{ 
                overflowY: 'auto', 
                padding: '20px',
                paddingBottom: '80px',
                display: 'block' 
              }}>
                {/* Only show game content if we have a selected game */}
                {selectedGame && (
                  <>
                {/* Game Result Section */}
                <div style={{ 
                  padding: '15px', 
                  marginBottom: '15px', 
                  borderRadius: '8px',
                  backgroundColor: selectedGame.result === 'win' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                  border: `1px solid ${selectedGame.result === 'win' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <h4 style={{ margin: '0 0 5px', fontSize: '1.2rem' }}>
                      {selectedGame.result === 'win' ? 'Victory' : 'Defeat'}
                    </h4>
                    <div>
                      You {selectedGame.wentFirst ? 'went first' : 'went second'}
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '1.8rem', 
                    fontWeight: 'bold',
                    color: selectedGame.result === 'win' ? '#4CAF50' : '#F44336' 
                  }}>
                    {selectedGame.result === 'win' ? '✓' : '✗'}
                  </div>
                </div>
                
                {/* Deck Information */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                    <i className="fas fa-layer-group"></i> Deck Information
                  </h4>
                  <div className="deck-info-container" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                      <strong>Your Deck:</strong> 
                      <span>{userDeckName}</span>
                      {playerDeckSprite && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <img 
                            src={playerDeckSprite} 
                            alt="Your Deck Sprite" 
                            style={{ 
                              width: 36, 
                              height: 36,
                              borderRadius: '4px',
                              border: '1px solid rgba(0,0,0,0.1)',
                              backgroundColor: 'rgba(255,255,255,0.5)'
                            }} 
                          />
                          {playerSecondaryDeckSprite && (
                            <img 
                              src={playerSecondaryDeckSprite} 
                              alt="Your Secondary Deck Sprite" 
                              style={{ 
                                width: 36, 
                                height: 36,
                                borderRadius: '4px',
                                border: '1px solid rgba(0,0,0,0.1)',
                                backgroundColor: 'rgba(255,255,255,0.5)'
                              }} 
                            />
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                      <strong>Opponent's Deck:</strong> 
                      <span>{opponentDeck || 'Unknown'}</span>
                      {opponentDeckVariant && (
                        <span style={{ fontStyle: 'italic', color: 'var(--text-muted)', fontSize: '0.9em' }}>
                          ({opponentDeckVariant})
                        </span>
                      )}
                      {opponentDeckSprite && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <img 
                            src={opponentDeckSprite} 
                            alt="Opponent Deck Sprite" 
                            style={{ 
                              width: 36, 
                              height: 36,
                              borderRadius: '4px',
                              border: '1px solid rgba(0,0,0,0.1)',
                              backgroundColor: 'rgba(255,255,255,0.5)'
                            }} 
                          />
                          {opponentSecondaryDeckSprite && (
                            <img 
                              src={opponentSecondaryDeckSprite} 
                              alt="Opponent Secondary Deck Sprite" 
                              style={{ 
                                width: 36, 
                                height: 36,
                                borderRadius: '4px',
                                border: '1px solid rgba(0,0,0,0.1)',
                                backgroundColor: 'rgba(255,255,255,0.5)'
                              }} 
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Rest of modal content remains the same */}
                {/* Coin Flip Section */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                    <i className="fas fa-coins"></i> Game Start Details
                  </h4>
                  <div>
                    <p>You {selectedGame.coinFlipWin ? 'won' : 'lost'} the coin flip</p>
                    
                    {selectedGame.coinFlipChoice && (
                      <p>You chose to {selectedGame.coinFlipChoice}</p>
                    )}
                    
                    <p>You went {selectedGame.wentFirst ? 'first' : 'second'}</p>
                    
                    {(selectedGame.playerMulligans || selectedGame.playerMulligans === 0) && (
                      <p>Your mulligans: {selectedGame.playerMulligans}</p>
                    )}
                    
                    {(selectedGame.opponentMulligans || selectedGame.opponentMulligans === 0) && (
                      <p>Opponent's mulligans: {selectedGame.opponentMulligans}</p>
                    )}
                    
                    {selectedGame.conceded && (
                      <p><strong>{selectedGame.conceded === 'Player' ? 'You' : 'Opponent'} conceded the game</strong></p>
                    )}
                  </div>
                </div>

                {/* Prize Details */}
                {selectedGame.advancedDetails && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                      <i className="fas fa-trophy"></i> Prize Cards
                    </h4>
                    
                    {selectedGame.advancedDetails.firstPrizeTakenBy && (
                      <div style={{ margin: '10px 0' }}>
                        <strong>First Prize Taken By:</strong> {selectedGame.advancedDetails.firstPrizeTakenBy}
                      </div>
                    )}
                    
                    {selectedGame.advancedDetails.prizeOrder && selectedGame.advancedDetails.prizeOrder.some(p => p !== null) && (
                      <div style={{ margin: '10px 0' }}>
                        <strong>Prize Order:</strong>{' '}
                        {selectedGame.advancedDetails.prizeOrder
                          .filter(p => p !== null)
                          .map(p => p === 1 ? 'Player' : p === 2 ? 'Opponent' : p)
                          .join(' → ')}
                        {' '}
                        <span style={{ color: 'var(--text-light)', fontSize: '0.9em' }}>
                          (Player: {selectedGame.advancedDetails.prizeOrder.filter(p => p === 1).length},
                          Opponent: {selectedGame.advancedDetails.prizeOrder.filter(p => p === 2).length})
                        </span>
                      </div>
                    )}
                    
                    {selectedGame.advancedDetails.prizeContents && selectedGame.advancedDetails.prizeContents.some(p => p) && (
                      <div style={{ margin: '10px 0' }}>
                        <strong>Prize Contents:</strong>
                        <ul style={{ margin: '10px 0 0', padding: '0 0 0 20px' }}>
                          {selectedGame.advancedDetails.prizeContents
                            .filter(content => content)
                            .map((content, i) => (
                              <li key={i}>{content}</li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Game Notes */}
                {selectedGame.notes && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                      <i className="far fa-sticky-note"></i> Game Notes
                    </h4>
                    <div style={{ 
                      whiteSpace: 'pre-wrap',
                      backgroundColor: 'var(--link-hover-bg)',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)'
                    }}>
                      {selectedGame.notes}
                    </div>
                  </div>
                )}
                  </>
                )}
              </div>
              
              <div className="notes-modal-footer">
                <button className="btn-cancel" onClick={closeGameDetailsModal}>Close</button>
              </div>
            </div>
          </div>,
          document.getElementById('modal-root') || document.body);
        })()}
        
        {showEditNotesModal &&
          <EditNotesModal
            isOpen={true}
            onClose={() => setShowEditNotesModal(false)}
            notes={notes || ''}
            onSave={handleSaveNotes}
            title="Game Notes"
          />
        }
        
        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={() => handleDelete(_id)}
          title="Delete Match"
          message="Are you sure you want to delete this match? This action cannot be undone."
        />
      </div>
    );
  }

  // Original UI
  return (
    <div
      className="match-item"
      onClick={() => {
        if (games && games.length > 0) {
          setSelectedGame({...games[0], gameNumber: 1});
        } else {
          setSelectedGame({ gameNumber: 1 });
        }
        setShowGameDetailsModal(true);
      }}
      style={{ borderLeftColor: `var(--${matchResult.status}-color)` }}
    >
      {/* Delete Button */}
      {(deleteMatch || deleteTCGMatch) && (
        <button
          className="btn btn-danger"
          style={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            padding: '0.25rem 0.5rem',
            fontSize: '0.75rem',
            borderRadius: '999px',
            display: 'inline-flex',
            alignItems: 'center'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteConfirm(true);
          }}
        >
          <i className="fas fa-trash-alt" style={{ marginRight: '0.25rem' }} />
          Delete
        </button>
      )}

      {/* Top header with match result and delete button */}
      <div className="match-header">
        <div>
          <span className={`badge badge-${matchResult.status}`}>
            {matchResult.result} {matchResult.score}
          </span>
          <span className="badge badge-secondary" style={{ marginLeft: '0.5rem' }}>
            TCG • {format || 'Bo1'}
          </span>
          {group && (
            <span className="group-badge" style={{ marginLeft: '0.5rem' }}>
              <i className="fas fa-object-group"></i> {group.name}
            </span>
          )}
          {tags && tags.length > 0 && tags.map((tag, index) => (
            <span key={index} className="match-tag-badge" style={{ marginLeft: '0.5rem' }}>
              <i className="fas fa-tag"></i> {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Deck information row */}
      <div className="deck-comparison" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginTop: '0.5rem',
        fontSize: '0.9rem'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap'
        }}>
          <span className="user-deck-label">Your Deck:</span> 
          <span>{userDeckName}</span>
          {/* Player's deck sprites */}
          {playerDeckSprite && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <img 
                src={playerDeckSprite} 
                alt="Your Deck Sprite" 
                style={{ 
                  width: 32, 
                  height: 32,
                  borderRadius: '4px',
                  border: '1px solid rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(255,255,255,0.5)'
                }} 
              />
              {playerSecondaryDeckSprite && (
                <img 
                  src={playerSecondaryDeckSprite} 
                  alt="Your Secondary Deck Sprite" 
                  style={{ 
                    width: 32, 
                    height: 32,
                    borderRadius: '4px',
                    border: '1px solid rgba(0,0,0,0.1)',
                    backgroundColor: 'rgba(255,255,255,0.5)'
                  }} 
                />
              )}
            </div>
          )}
        </div>
        <span className="deck-vs-divider" style={{ opacity: 0.6, margin: '0 0.5rem' }}>vs</span>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '0.5rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="user-deck-label">Opponent's Deck:</span> 
              <span>{opponentDeck || 'Unknown'}</span>
            </div>
            {opponentDeckVariant && (
              <span style={{ 
                fontStyle: 'italic', 
                color: 'var(--text-muted)', 
                fontSize: '0.85em',
                marginTop: '2px'
              }}>
                {opponentDeckVariant}
              </span>
            )}
          </div>
          {/* Opponent's deck sprites */}
          {opponentDeckSprite && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <img 
                src={opponentDeckSprite} 
                alt="Opponent Deck Sprite" 
                style={{ 
                  width: 32, 
                  height: 32,
                  borderRadius: '4px',
                  border: '1px solid rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(255,255,255,0.5)'
                }} 
              />
              {opponentSecondaryDeckSprite && (
                <img 
                  src={opponentSecondaryDeckSprite} 
                  alt="Opponent Secondary Deck Sprite" 
                  style={{ 
                    width: 32, 
                    height: 32,
                    borderRadius: '4px',
                    border: '1px solid rgba(0,0,0,0.1)',
                    backgroundColor: 'rgba(255,255,255,0.5)'
                  }} 
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded View - Removed, now opens modal directly */}
      {false && (
        <>
          <div className="match-info" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: '1rem',
            borderTop: '1px solid var(--border-color)',
            paddingTop: '0.75rem'
          }}>
            <div className="match-date">
              <i className="far fa-calendar-alt" /> {formatDate(createdAt)}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Format: <strong>{format || 'Standard'}</strong>
            </div>
          </div>
          
          {/* Games overview - compact view */}
          <div className="games-grid" style={{ 
            display: 'grid', 
            gridTemplateColumns: `repeat(auto-fit, minmax(${games?.length > 1 ? '220px' : '100%'}, 1fr))`, 
            gap: '1rem',
            marginTop: '0.75rem',
            position: 'relative'
          }}>
            {games?.map((game, index) => (
              <div
                key={index}
                className={`game-item ${game.result === 'win' ? 'game-win' : 'game-loss'}`}
                style={{ 
                  padding: '0.65rem',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  cursor: 'pointer'
                }}
                onClick={(e) => handleGameClick(e, game, index)}
              >
                <div className="game-header" style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  fontWeight: 'bold', 
                  fontSize: '0.9rem',
                  marginBottom: '0.4rem'
                }}>
                  <span>Game {index + 1}</span>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                    <i className="fas fa-search-plus"></i> View Details
                  </span>
                </div>
                
                <div className="game-preview-meta">
  <span className={`badge badge-${game.result === 'win' ? 'success' : 'danger'}`}>
    {game.result === 'win' ? 'Won' : 'Lost'}
  </span>
  <span>{game.wentFirst ? 'Went First' : 'Went Second'}</span>
  {index === 0 && (
    <span>Coin flip: <strong>{game.coinFlipWin ? 'Won' : 'Lost'}</strong></span>
  )}
  {game.playerMulligans > 0 && (
    <span>Your mulligans: {game.playerMulligans}</span>
  )}
  {game.opponentMulligans > 0 && (
    <span>Opp. mulligans: {game.opponentMulligans}</span>
  )}
  {game.conceded && (
    <span>{game.conceded === 'Player' ? 'You conceded' : 'Opponent conceded'}</span>
  )}
  {game.donk && <span className="donk-label">Donk</span>}
  {game.advancedDetails && (
    <span className="details-label">
      <i className="fas fa-info-circle"></i> Advanced details available
    </span>
  )}
</div>
              </div>
            ))}
          </div>

        </>
      )}
      
      {/* Game Details Modal */}
      {showGameDetailsModal && selectedGame && (() => {
        // Try to find the modal-root element
        const modalRoot = document.getElementById('modal-root');
        
        // If modal-root doesn't exist, create it dynamically
        if (!modalRoot) {
          console.warn("modal-root element not found, creating it dynamically");
          const newModalRoot = document.createElement('div');
          newModalRoot.id = 'modal-root';
          document.body.appendChild(newModalRoot);
        }
        
        return ReactDOM.createPortal(
        <div className="notes-modal-backdrop" onClick={closeGameDetailsModal}>
          <div 
            className="notes-modal" 
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '700px' }}
          >
            <div className="notes-modal-header">
              <h3>Match Details</h3>
              <div className="notes-modal-controls">
                <button 
                  className="notes-modal-control-btn" 
                  onClick={handleEditMatch} 
                  title="Edit Match"
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button className="notes-modal-close" onClick={closeGameDetailsModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            
            {/* Game Tabs for multi-game matches */}
            {games && games.length > 1 && (
              <div style={{
                display: 'flex',
                borderBottom: '2px solid var(--border-color)',
                backgroundColor: 'var(--bg-secondary)',
                padding: '0 20px',
                gap: '10px'
              }}>
                {games.map((game, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedGame({...game, gameNumber: index + 1})}
                    style={{
                      padding: '12px 20px',
                      border: 'none',
                      backgroundColor: selectedGame?.gameNumber === index + 1 ? 'var(--bg-primary)' : 'transparent',
                      color: selectedGame?.gameNumber === index + 1 ? 'var(--text-primary)' : 'var(--text-secondary)',
                      borderBottom: selectedGame?.gameNumber === index + 1 ? '2px solid var(--link-color)' : '2px solid transparent',
                      marginBottom: '-2px',
                      cursor: 'pointer',
                      fontWeight: selectedGame?.gameNumber === index + 1 ? 'bold' : 'normal',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedGame?.gameNumber !== index + 1) {
                        e.target.style.backgroundColor = 'var(--link-hover-bg)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedGame?.gameNumber !== index + 1) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span>Game {index + 1}</span>
                    <span style={{
                      fontSize: '0.8rem',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      backgroundColor: game.result === 'win' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                      color: game.result === 'win' ? '#4CAF50' : '#F44336'
                    }}>
                      {game.result === 'win' ? 'W' : 'L'}
                    </span>
                  </button>
                ))}
              </div>
            )}
            
            <div className="notes-modal-body tcg-modal-body" style={{ 
              overflowY: 'auto', 
              padding: '20px',
              paddingBottom: '80px', /* Add extra padding at the bottom for better mobile scrolling */
              display: 'block' 
            }}>
              {/* Only show game content if we have a selected game */}
              {selectedGame && (
                <>
              {/* Game Result Section */}
              <div style={{ 
                padding: '15px', 
                marginBottom: '15px', 
                borderRadius: '8px',
                backgroundColor: selectedGame.result === 'win' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                border: `1px solid ${selectedGame.result === 'win' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h4 style={{ margin: '0 0 5px', fontSize: '1.2rem' }}>
                    {selectedGame.result === 'win' ? 'Victory' : 'Defeat'}
                  </h4>
                  <div>
                    You {selectedGame.wentFirst ? 'went first' : 'went second'}
                  </div>
                </div>
                <div style={{ 
                  fontSize: '1.8rem', 
                  fontWeight: 'bold',
                  color: selectedGame.result === 'win' ? '#4CAF50' : '#F44336' 
                }}>
                  {selectedGame.result === 'win' ? '✓' : '✗'}
                </div>
              </div>
              
              {/* Deck Information */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                  <i className="fas fa-layer-group"></i> Deck Information
                </h4>
                <div className="deck-info-container" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <strong>Your Deck:</strong> 
                    <span>{userDeckName}</span>
                    {/* Player deck sprites in modal */}
                    {playerDeckSprite && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <img 
                          src={playerDeckSprite} 
                          alt="Your Deck Sprite" 
                          style={{ 
                            width: 36, 
                            height: 36,
                            borderRadius: '4px',
                            border: '1px solid rgba(0,0,0,0.1)',
                            backgroundColor: 'rgba(255,255,255,0.5)'
                          }} 
                        />
                        {playerSecondaryDeckSprite && (
                          <img 
                            src={playerSecondaryDeckSprite} 
                            alt="Your Secondary Deck Sprite" 
                            style={{ 
                              width: 36, 
                              height: 36,
                              borderRadius: '4px',
                              border: '1px solid rgba(0,0,0,0.1)',
                              backgroundColor: 'rgba(255,255,255,0.5)'
                            }} 
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <strong>Opponent's Deck:</strong> 
                    <span>{opponentDeck || 'Unknown'}</span>
                    {opponentDeckVariant && (
                      <span style={{ fontStyle: 'italic', color: 'var(--text-muted)', fontSize: '0.9em' }}>
                        ({opponentDeckVariant})
                      </span>
                    )}
                    {/* Opponent deck sprites in modal */}
                    {opponentDeckSprite && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <img 
                          src={opponentDeckSprite} 
                          alt="Opponent Deck Sprite" 
                          style={{ 
                            width: 36, 
                            height: 36,
                            borderRadius: '4px',
                            border: '1px solid rgba(0,0,0,0.1)',
                            backgroundColor: 'rgba(255,255,255,0.5)'
                          }} 
                        />
                        {opponentSecondaryDeckSprite && (
                          <img 
                            src={opponentSecondaryDeckSprite} 
                            alt="Opponent Secondary Deck Sprite" 
                            style={{ 
                              width: 36, 
                              height: 36,
                              borderRadius: '4px',
                              border: '1px solid rgba(0,0,0,0.1)',
                              backgroundColor: 'rgba(255,255,255,0.5)'
                            }} 
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Coin Flip Section */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                  <i className="fas fa-coins"></i> Game Start Details
                </h4>
                <div>
                  <p>You {selectedGame.coinFlipWin ? 'won' : 'lost'} the coin flip</p>
                  
                  {selectedGame.coinFlipChoice && (
                    <p>You chose to {selectedGame.coinFlipChoice}</p>
                  )}
                  
                  <p>You went {selectedGame.wentFirst ? 'first' : 'second'}</p>
                  
                  {(selectedGame.playerMulligans || selectedGame.playerMulligans === 0) && (
                    <p>Your mulligans: {selectedGame.playerMulligans}</p>
                  )}
                  
                  {(selectedGame.opponentMulligans || selectedGame.opponentMulligans === 0) && (
                    <p>Opponent's mulligans: {selectedGame.opponentMulligans}</p>
                  )}
                  
                  {selectedGame.conceded && (
                    <p><strong>{selectedGame.conceded === 'Player' ? 'You' : 'Opponent'} conceded the game</strong></p>
                  )}
                </div>
              </div>

              {/* Prize Details */}
              {selectedGame.advancedDetails && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                    <i className="fas fa-trophy"></i> Prize Cards
                  </h4>
                  
                  {selectedGame.advancedDetails.firstPrizeTakenBy && (
                    <div style={{ margin: '10px 0' }}>
                      <strong>First Prize Taken By:</strong> {selectedGame.advancedDetails.firstPrizeTakenBy}
                    </div>
                  )}
                  
                  {selectedGame.advancedDetails.prizeOrder && selectedGame.advancedDetails.prizeOrder.some(p => p !== null) && (
                    <div style={{ margin: '10px 0' }}>
                      <strong>Prize Order:</strong>{' '}
                      {selectedGame.advancedDetails.prizeOrder
                        .filter(p => p !== null)
                        .map(p => p === 1 ? 'Player' : p === 2 ? 'Opponent' : p)
                        .join(' → ')}
                      {' '}
                      <span style={{ color: 'var(--text-light)', fontSize: '0.9em' }}>
                        (Player: {selectedGame.advancedDetails.prizeOrder.filter(p => p === 1).length},
                        Opponent: {selectedGame.advancedDetails.prizeOrder.filter(p => p === 2).length})
                      </span>
                    </div>
                  )}
                  
                  {selectedGame.advancedDetails.prizeContents && selectedGame.advancedDetails.prizeContents.some(p => p) && (
                    <div style={{ margin: '10px 0' }}>
                      <strong>Prize Contents:</strong>
                      <ul style={{ margin: '10px 0 0', padding: '0 0 0 20px' }}>
                        {selectedGame.advancedDetails.prizeContents
                          .filter(content => content)
                          .map((content, i) => (
                            <li key={i}>{content}</li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {/* Game Notes */}
              {selectedGame.notes && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                    <i className="far fa-sticky-note"></i> Game Notes
                  </h4>
                  <div style={{ 
                    whiteSpace: 'pre-wrap',
                    backgroundColor: 'var(--link-hover-bg)',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)'
                  }}>
                    {selectedGame.notes}
                  </div>
                </div>
              )}
                </>
              )}
            </div>
            
            <div className="notes-modal-footer">
              <button className="btn-cancel" onClick={closeGameDetailsModal}>Close</button>
            </div>
          </div>
        </div>,
    document.getElementById('modal-root') || document.body);
      })()}
      
      {/* Notes Edit Modal */}
      {showEditNotesModal &&
        <EditNotesModal
          isOpen={true}
          onClose={() => setShowEditNotesModal(false)}
          notes={notes || ''}
          onSave={handleSaveNotes}
          title="Game Notes"
        />
      }
      
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => handleDelete(_id)}
        title="Delete Match"
        message="Are you sure you want to delete this match? This action cannot be undone."
      />
    </div>
  );
};

export default MatchItemTCG;